import { Card } from '@/shared/ui/atoms';
import { CopyButton } from '@/features/onboarding/components/CopyButton';
import { Prose } from '@/features/onboarding/components/Prose';
import { FIRST_RUN, PITFALLS, PREREQS } from '@/features/onboarding/data/setup';
import { REPOS } from '@/features/onboarding/data/repos';

function Commands({ commands }: { commands: string[] }): React.JSX.Element {
  return (
    <div className="relative rounded-md border border-line-soft bg-[#f6f8fa] p-3">
      <pre className="overflow-x-auto font-mono text-[12px] leading-5 text-ink">{commands.join('\n')}</pre>
      <span className="absolute top-2 right-2">
        <CopyButton value={commands.join('\n')} label="전체 복사" />
      </span>
    </div>
  );
}

/**
 * 개발 환경 — 설치 → 첫 기동 → 함정 순서. 함정을 맨 아래가 아니라 눈에 띄게 두는 이유는
 * 여기 적힌 것들이 "모르면 몇 시간", 알면 한 줄이기 때문이다.
 */
export function SetupView(): React.JSX.Element {
  return (
    <div className="flex flex-col gap-4">
      <Card title="📥 설치" hint="macOS 기준 · Homebrew">
        <ul className="flex flex-col">
          {PREREQS.map((prereq) => (
            <li key={prereq.name} className="border-b border-line-soft py-2.5 last:border-b-0">
              <div className="flex items-baseline gap-2">
                <strong className="text-[13px]">{prereq.name}</strong>
                <span className="text-[12px] text-muted"><Prose text={prereq.why} /></span>
              </div>
              <div className="mt-1.5 flex items-center gap-2">
                <code className="min-w-0 flex-1 truncate rounded bg-line-soft px-2 py-1 font-mono text-[11.5px]">
                  {prereq.install}
                </code>
                <CopyButton value={prereq.install} />
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="▶️ 첫 기동" hint="위에서부터 순서대로 진행해 주세요 — 2번까지 되면 환경은 대체로 정상입니다">
        <ol className="flex flex-col gap-3">
          {FIRST_RUN.map((step) => (
            <li key={step.title}>
              <div className="mb-1.5">
                <strong className="text-[13px]">{step.title}</strong>
                <span className="ml-2 text-[12px] text-muted"><Prose text={step.detail} /></span>
              </div>
              <Commands commands={step.commands} />
            </li>
          ))}
        </ol>
      </Card>

      <Card title="🕳️ 함정" hint="팀에서 실제로 시간을 잡아먹었던 것들입니다">
        <ul className="flex flex-col gap-2">
          {PITFALLS.map((pitfall) => (
            <li
              key={pitfall.symptom}
              className={`rounded-md border p-3 ${
                pitfall.severity === 'danger' ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'
              }`}
            >
              <div className="flex items-baseline gap-2">
                <span className={`text-[11px] font-bold ${pitfall.severity === 'danger' ? 'text-rose-700' : 'text-amber-800'}`}>
                  {pitfall.severity === 'danger' ? '🔥 남의 환경까지 깨짐' : '⚠️ 막힘'}
                </span>
                <strong className="text-[13px]"><Prose text={pitfall.symptom} /></strong>
              </div>
              <div className="mt-1 text-[12px] text-muted">
                <span className="font-semibold text-ink">왜</span> <Prose text={pitfall.cause} />
              </div>
              <div className="text-[12px] text-muted">
                <span className="font-semibold text-ink">대응</span> <Prose text={pitfall.fix} />
              </div>
            </li>
          ))}
        </ul>
      </Card>

      <Card title="🧾 저장소별 기동 명령" hint=".nvmrc가 저장소마다 다릅니다 — 디렉토리를 옮기면 nvm use를 실행해 주세요">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-[12.5px]">
            <thead className="text-[11px] text-muted uppercase">
              <tr className="border-b border-line">
                <th className="py-1.5 pr-3 font-semibold">저장소</th>
                <th className="py-1.5 pr-3 font-semibold">Node</th>
                <th className="py-1.5 pr-3 font-semibold">기동</th>
                <th className="py-1.5 font-semibold">포트</th>
              </tr>
            </thead>
            <tbody>
              {REPOS.map((repo) => (
                <tr key={repo.name} className="border-b border-line-soft last:border-b-0">
                  <td className="py-1.5 pr-3 font-semibold">{repo.name}</td>
                  <td className="py-1.5 pr-3 font-mono text-[11.5px] text-muted">{repo.node ?? '—'}</td>
                  <td className="py-1.5 pr-3 font-mono text-[11.5px]">{repo.run ?? '—'}</td>
                  <td className="py-1.5 tabular-nums text-muted">{repo.port ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
